import { ocrTextPanelSx, ocrTextSurfaceSx } from './ocrTextStyles';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, MenuItem, Stack, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import { ImagePreview, PDFPreview, DocxPreview } from './DocumentPreviewModal';
import { attachmentOcrRequest } from '../../utils/attachmentOcr';
import { pollSource } from '../../utils/reportSourcePreparation';

export default function AttachmentOcrDialog({ item, onClose, retrieveAttachmentFile, autoRun = false, queue = [], onSelectFile, inline = false, textOnly = false, renderHighlights, highlightTarget, onDirtyChange, onReviewStateChange }) {
  const wordSource = /\.docx$/i.test(item.path);
  const [editingHighlights, setEditingHighlights] = useState(false);
  useEffect(() => setEditingHighlights(false), [highlightTarget]);
  const showHighlights = Boolean(renderHighlights) && !editingHighlights;
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [sourceVerified, setSourceVerified] = useState(false);
  const [result, setResult] = useState(null);
  const [draft, setDraft] = useState([]);
  const [busy, setBusy] = useState('loading');
  const [error, setError] = useState('');
  const [autoSaveFailed, setAutoSaveFailed] = useState(false);
  const [notice, setNotice] = useState('');
  const [page, setPage] = useState(0);
  const [tab, setTab] = useState(1);
  const [discardOpen, setDiscardOpen] = useState(false);
  const history = useRef({ undo: [], redo: [] });
  const alive = useRef(true);
  const autoStarted = useRef(false);
  const requestController = useRef(null);
  const activeJob = useRef(null);
  const retrieveFile = useRef(retrieveAttachmentFile);
  retrieveFile.current = retrieveAttachmentFile;
  const saved = result?.reviewed_pages || result?.pages?.map(p => p.text) || [];
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const emptyDraft = result?.status === 'ready' && !draft.some(text => text.trim());
  const dirtyCallback = useRef(onDirtyChange);
  dirtyCallback.current = onDirtyChange;
  const reviewStateCallback = useRef(onReviewStateChange);
  reviewStateCallback.current = onReviewStateChange;
  useEffect(() => {
    dirtyCallback.current?.(item.id, dirty || busy === 'saving');
    return () => dirtyCallback.current?.(item.id, false);
  }, [item.id, dirty, busy]);
  useEffect(() => {
    reviewStateCallback.current?.(item.path, Boolean(result?.reviewed_pages && sourceVerified && !dirty && !busy));
  }, [item.path, result?.reviewed_pages, sourceVerified, dirty, busy]);

  const applyResult = useCallback((data) => {
    if (data.jobId) activeJob.current = data.jobId;
    setResult(data);
    setAutoSaveFailed(false);
    if (data.status === 'ready') {
      setDraft(data.reviewed_pages || data.pages.map(p => p.text));
      setPage(0);
      history.current = { undo: [], redo: [] };
    }
  }, []);

  const load = useCallback(async () => {
    setBusy('loading'); setError('');
    const controller = new AbortController();
    requestController.current = controller;
    try {
      const data = await pollSource(activeJob.current, { path: item.path }, controller.signal);
      if (alive.current) applyResult(data);
    } catch (e) {
      if (alive.current && e.name !== 'AbortError') setError(e.message);
    } finally {
      if (alive.current) setBusy('');
    }
  }, [item.path, applyResult]); // File identity is the path; title changes do not create another job.

  useEffect(() => {
    alive.current = true;
    let cancelled = false;
    retrieveFile.current(item.path, item.title)
      .then(value => { if (!cancelled) setFile(value); })
      .catch(() => { if (!cancelled) setFileError('The original file could not be opened. Close and try again.'); });
    load();
    return () => {
      cancelled = true; alive.current = false;
      requestController.current?.abort();
    };
  }, [item.path, item.title, load]);

  useEffect(() => {
    if (result?.status !== 'processing' || busy) return undefined;
    const timer = setTimeout(load, 3000);
    return () => clearTimeout(timer);
  }, [result, busy, load]);

  useEffect(() => {
    let cancelled = false;
    setSourceVerified(false);
    if (!file || !result?.source_hash) return undefined;
    (async () => {
      const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
      const hash = Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');
      if (cancelled) return;
      if (hash !== result.source_hash) setFileError('The original file has changed. Close this window and open OCR again.');
      else setSourceVerified(true);
    })().catch(() => { if (!cancelled) setFileError('The original file could not be verified. Close and try again.'); });
    return () => { cancelled = true; };
  }, [file, result?.source_hash]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const run = async () => {
    setBusy('running'); setError(''); setNotice('');
    const controller = new AbortController();
    requestController.current = controller;
    try {
      const data = await attachmentOcrRequest('POST', item.path, {}, controller.signal);
      if (alive.current) applyResult(data);
    } catch (e) {
      if (alive.current && e.name !== 'AbortError') {
        setError(e.message);
        setResult({ status: 'failed' });
      }
    } finally {
      if (alive.current) setBusy('');
    }
  };

  useEffect(() => {
    if (autoRun && !autoStarted.current && result?.status === 'not_started' && file && !fileError && !busy) {
      autoStarted.current = true;
      run();
    }
  });

  const save = useCallback(async (pages) => {
    const snapshot = [...pages];
    setBusy('saving'); setError(''); setNotice(''); setAutoSaveFailed(false);
    const controller = new AbortController();
    requestController.current = controller;
    try {
      const data = await attachmentOcrRequest('PATCH', item.path,
        { id: result.id, revision: result.revision, pages: snapshot }, controller.signal);
      if (alive.current) {
        setResult(data);
        setDraft(current => JSON.stringify(current) === JSON.stringify(snapshot)
          ? (data.reviewed_pages || snapshot) : current);
      }
    } catch (e) {
      if (alive.current && e.name !== 'AbortError') { setError(e.message); setAutoSaveFailed(true); }
    } finally {
      if (alive.current) setBusy('');
    }
  }, [item.path, result?.id, result?.revision]);

  useEffect(() => {
    if (result?.status !== 'ready' || !sourceVerified || busy || !draft.some(text => text.trim())
      || (result.reviewed_pages && !dirty) || autoSaveFailed) return undefined;
    const timer = setTimeout(() => save(draft), 800);
    return () => clearTimeout(timer);
  }, [result?.status, result?.reviewed_pages, sourceVerified, busy, draft, dirty, autoSaveFailed, save]);

  const close = () => { if (busy === 'saving') return; dirty ? setDiscardOpen('close') : onClose(); };
  const processing = busy === 'running' || result?.status === 'processing';
  const ready = result?.status === 'ready';
  const currentText = draft[page] || '';
  const selectedIndex = queue.findIndex(entry => entry.path === item.path);
  const chooseFile = path => { if (path === item.path) return; if (dirty) setDiscardOpen({ path }); else onSelectFile(path); };
  const copyAllText = async () => {
    try { await navigator.clipboard.writeText(draft.join('\n\n')); setNotice('Text copied.'); }
    catch { setError('Copy is unavailable. Select and copy the text manually.'); }
  };
  const changePageText = value => {
    if (value === currentText) return;
    setAutoSaveFailed(false);
    history.current.undo.push([...draft]);
    if (history.current.undo.length > 100) history.current.undo.shift();
    history.current.redo = [];
    setDraft(values => values.map((text, i) => i === page ? value : text));
  };
  const undo = () => {
    if (!history.current.undo.length) return;
    setAutoSaveFailed(false);
    history.current.redo.push([...draft]);
    setDraft(history.current.undo.pop());
  };
  const redo = () => {
    if (!history.current.redo.length) return;
    setAutoSaveFailed(false);
    history.current.undo.push([...draft]);
    setDraft(history.current.redo.pop());
  };

  const content = <>
      {!inline && <DialogTitle sx={{ px: 3, py: 2, borderBottom: '1px solid #E6ECEF', mb: 2, bgcolor: '#fff' }}>
        <Typography component="span" sx={{ display: 'block', fontSize: 11, color: '#84929F', mb: .5 }}>Original & text</Typography>
        <Typography component="span" sx={{ display: 'block', fontSize: 17, fontWeight: 600, color: '#293E4B', overflowWrap: 'anywhere' }}>{item.title}</Typography>
      </DialogTitle>}
      {queue.length > 1 && (inline
        ? queue.length <= 4
          ? <Tabs value={item.path} onChange={(_, path) => chooseFile(path)} variant="scrollable" scrollButtons="auto" aria-label="Selected source files" sx={{ px: 2, borderBottom: '1px solid #E6ECEF', flexShrink: 0, '& .MuiTab-root': { textTransform: 'none', maxWidth: 260, minWidth: 0, alignItems: 'flex-start' } }}>
              {queue.map(entry => <Tab key={entry.path} value={entry.path} disabled={Boolean(busy)} label={entry.title} title={entry.title} />)}
            </Tabs>
          : <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderBottom: '1px solid #E6ECEF', minWidth: 0 }}>
              <Typography sx={{ fontSize: 12, color: '#617380', whiteSpace: 'nowrap' }}>Source {selectedIndex + 1} of {queue.length}</Typography>
              <Autocomplete size="small" options={queue} value={queue[selectedIndex] || null} disabled={Boolean(busy)}
                getOptionLabel={entry => entry.title || ''} isOptionEqualToValue={(option, value) => option.path === value.path}
                onChange={(_, entry) => { if (entry) chooseFile(entry.path); }}
                renderOption={(props, entry) => <li {...props} key={entry.path}>{queue.indexOf(entry) + 1}. {entry.title}</li>}
                renderInput={params => <TextField {...params} label="Find a source file" inputProps={{ ...params.inputProps, 'aria-label': 'Find a source file' }} />}
                sx={{ flex: 1, minWidth: 0, maxWidth: 480 }} />
              <Tooltip title="Previous source"><span><IconButton aria-label="Previous source" size="small" disabled={Boolean(busy) || selectedIndex <= 0} onClick={() => chooseFile(queue[selectedIndex - 1].path)}><ChevronLeftIcon /></IconButton></span></Tooltip>
              <Tooltip title="Next source"><span><IconButton aria-label="Next source" size="small" disabled={Boolean(busy) || selectedIndex < 0 || selectedIndex >= queue.length - 1} onClick={() => chooseFile(queue[selectedIndex + 1].path)}><ChevronRightIcon /></IconButton></span></Tooltip>
            </Box>
        : <TextField select label="Uploaded files" value={item.path} disabled={Boolean(busy)} sx={{ mx: 3, mb: 1 }}
          onChange={event => chooseFile(event.target.value)}>
          {queue.map(entry => <MenuItem key={entry.path} value={entry.path}>{entry.title}</MenuItem>)}
        </TextField>)}
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1, minHeight: 0, ...(inline ? { p: 0, '&:first-of-type': { pt: 0 } } : {}) }}>
        {error && <Alert severity="error" action={autoSaveFailed && <Button size="small" onClick={() => save(draft)}>Retry save</Button>}>{error}</Alert>}
        {textOnly && fileError && <Alert severity="error">{fileError}</Alert>}
        {notice && <Alert severity="success">{notice}</Alert>}
        {emptyDraft && <Alert severity="warning">OCR text needs some readable content before it can be saved. Undo the edit or enter text to continue.</Alert>}
        {!textOnly && <Tabs variant="fullWidth" value={tab} onChange={(_, value) => setTab(value)} sx={{
          display: { xs: 'flex', md: 'none' }, flexShrink: 0,
          '& .MuiTab-root': { minWidth: 0, textTransform: 'none', px: 1 },
        }}>
          <Tab label="Original file" /><Tab label="Recognized text" />
        </Tabs>}
        <Box sx={{ display: 'flex', flex: 1, gap: 0, minHeight: 0 }}>
          {!textOnly && <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: { xs: tab === 0 ? 'flex' : 'none', md: 'flex' }, flexDirection: 'column', borderRight: '1px solid #E3E9ED', borderRadius: 0, bgcolor: '#fff', overflow: 'hidden' }}>
            <Typography sx={{ px: 2, py: 1.5, fontSize: 12, fontWeight: 600, color: '#5F7280', borderBottom: '1px solid #EDF1F4' }}>Original file</Typography>
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              {fileError ? <Alert severity="error">{fileError}</Alert> : !file ? <CircularProgress sx={{ m: 2 }} /> :
                /\.pdf$/i.test(item.path) ? <PDFPreview file={file} page={page} onPageChange={setPage} /> : wordSource ? <DocxPreview file={file} /> : <ImagePreview file={file} fitWidth />}
            </Box>
          </Box>}
          <Box sx={{ ...ocrTextPanelSx, display: textOnly ? 'flex' : { xs: tab === 1 ? 'flex' : 'none', md: 'flex' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', flexShrink: 0 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#5F7280' }}>Recognized text {ready && (autoSaveFailed ? '• Save failed' : !sourceVerified ? '• Checking file…' : emptyDraft ? '• Not saved' : busy === 'saving' || dirty || !result.reviewed_pages ? '• Saving…' : '• Saved')}</Typography>
              {ready && <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                <Tooltip title="Undo edit"><span><IconButton size="small" aria-label="Undo OCR edit" disabled={!history.current.undo.length || !sourceVerified} onClick={undo}><UndoIcon fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title="Redo edit"><span><IconButton size="small" aria-label="Redo OCR edit" disabled={!history.current.redo.length || !sourceVerified} onClick={redo}><RedoIcon fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title="Copy text from all pages"><IconButton size="small" aria-label="Copy all OCR text" onClick={copyAllText}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
              </Stack>}
            </Box>
            {ready ? <>
              {renderHighlights && <Button size="small" onClick={() => setEditingHighlights(value => !value)} sx={{ alignSelf: 'flex-start', textTransform: 'none' }}>{showHighlights ? 'Edit text' : 'Show source matches'}</Button>}
              {showHighlights ? <Box role="region" aria-label="Recognized text" sx={{ ...ocrTextSurfaceSx, whiteSpace: 'pre-wrap' }}>{renderHighlights(draft)}</Box> : <>
              <TextField select size="small" label={wordSource ? 'Document' : 'Page'} value={Math.min(page, result.pages.length - 1)} onChange={e => setPage(Number(e.target.value))}>
                {result.pages.map((p, i) => <MenuItem key={p.page} value={i}>{wordSource ? 'Word document text' : `Page ${p.page}`}{p.method === 'text' ? ' — extracted text' : ''}</MenuItem>)}
              </TextField>
              <Typography variant="caption">Check names and numbers against the original. OCR text and edits save automatically for reports.</Typography>
              <Box component="textarea" value={currentText} disabled={!sourceVerified}
                aria-label="Recognized text" maxLength={100000}
                onChange={e => changePageText(e.target.value)}
                onKeyDown={event => {
                  if (!(event.metaKey || event.ctrlKey)) return;
                  const key = event.key.toLowerCase();
                  if (key === 'z' && (event.shiftKey ? history.current.redo.length : history.current.undo.length)) {
                    event.preventDefault(); (event.shiftKey ? redo : undo)();
                  } else if (key === 'y' && history.current.redo.length) { event.preventDefault(); redo(); }
                }}
                sx={ocrTextSurfaceSx} />
              </>}
            </> : <Box sx={{ pt: 2, textAlign: 'center', px: 2 }}>
              {(busy || processing) && <CircularProgress size={28} />}
              <Typography sx={{ my: 2 }}>{processing ? `Recognizing text… ${result?.totalPages ? `${result.completedPages || 0}/${result.totalPages} pages. ` : ''}You can close this window and check again later.` :
                busy ? 'Checking whether this file already has OCR text…' : 'OCR has not been run for this file. Select Run OCR to recognize its text.'}</Typography>
              {result?.status === 'failed' && !error && <Alert severity="warning">The previous attempt did not finish. Please try again.</Alert>}
              <Typography variant="caption" display="block" sx={{ mb: 2 }}>PDF (up to 50 pages), JPG or PNG. Maximum file size: 15 MiB.</Typography>
              <Button variant="contained" disabled={Boolean(busy) || processing || !file || Boolean(fileError)} onClick={run}>
                {result?.status === 'failed' ? 'Retry OCR' : 'Run OCR'}
              </Button>
            </Box>}
          </Box>
        </Box>
      </DialogContent>
      {!inline && <DialogActions sx={{ px: 3, py: 2, bgcolor: '#fff', borderTop: '1px solid #E6ECEF', '& .MuiButton-root': { textTransform: 'none', borderRadius: '8px', fontSize: 12 }, '& .MuiButton-contained': { bgcolor: '#218C89', boxShadow: 'none', '&:hover': { bgcolor: '#197572' } } }}>
        <Button onClick={close} disabled={busy === 'saving'}>Close</Button>
      </DialogActions>}
  </>;
  return <>
    {inline ? <Box sx={{ flex: '0 0 auto', height: { xs: '70vh', md: 'min(72vh, 780px)' }, minHeight: { xs: 440, md: 520 }, display: 'flex', flexDirection: 'column', bgcolor: '#F8FAFB', overflow: 'hidden' }}>{content}</Box>
      : <Dialog open onClose={close} maxWidth={false} fullWidth PaperProps={{ sx: {
        width: { xs: 'calc(100% - 16px)', md: '96vw' },
        m: { xs: 1, md: 4 }, height: { xs: 'calc(100% - 16px)', md: '92vh' },
        maxHeight: { xs: 'calc(100% - 16px)', md: 'calc(100% - 64px)' }, maxWidth: 1440, borderRadius: '16px', bgcolor: '#F8FAFB',
      } }}>{content}</Dialog>}
    <Dialog open={Boolean(discardOpen)} onClose={() => setDiscardOpen(false)}>
      <DialogTitle>Discard unsaved OCR edits?</DialogTitle>
      <DialogActions><Button onClick={() => setDiscardOpen(false)}>Keep editing</Button><Button onClick={() => {
        if (discardOpen.path) { onSelectFile(discardOpen.path); } else onClose();
      }}>Discard edits</Button></DialogActions>
    </Dialog>
  </>;
}
